<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-3 py-3 sm:grid-cols-1">
        <BaseInputRadio
            v-model="formValues.us_entity"
            name="fatca_declaration.us_entity"
            :options="yesNoOptions"
            :required="isRequiredLegalComplianceProfileAttribute('fatca_declaration', 'us_entity')"
            :label="$t('fatca_declaration.us_entity')" />
        <InlineAlert
            v-if="formValues.us_entity === 'yes'"
            class="mt-4"
            :title="$t('fatca_declaration.cannot_continue')"
            description="As you are a U.S. person, you cannot complete the KYC Form online using this Wizard. Please contact Venionaire at platform@superangels.club"
            type="Error">
        </InlineAlert>
        <BaseInputRadio
            v-if="formValues.us_entity === 'no'"
            v-model="formValues.financial_institutions_under_fatca"
            name="fatca_declaration.financial_institutions_under_fatca"
            :options="yesNoOptions"
            :required="
                isRequiredLegalComplianceProfileAttribute('fatca_declaration', 'financial_institutions_under_fatca')
            "
            :label="$t('fatca_declaration.financial_institutions_under_fatca')" />
        <BaseInputSelect
            v-if="formValues.us_entity === 'no' && formValues.financial_institutions_under_fatca === 'no'"
            v-model="formValues.non_financial_entity_reason"
            name="fatca_declaration.non_financial_entity_reason"
            :options="nfeOptions"
            :label="$t('fatca_declaration.non_financial_entity_reason')" />
        <BaseInputSelect
            v-if="formValues.us_entity === 'no' && formValues.financial_institutions_under_fatca === 'yes'"
            v-model="formValues.financial_institution_category"
            name="fatca_declaration.financial_institution_category"
            :options="financialInstitutionCategoryOptions"
            :label="$t('fatca_declaration.financial_institution_category')" />

        <BaseInputRadio
            v-if="
                formValues.us_entity === 'no' &&
                formValues.financial_institutions_under_fatca === 'yes' &&
                formValues.financial_institution_category
            "
            v-model="formValues.provide_giin"
            name="fatca_declaration.provide_giin"
            :options="yesNoOptions"
            :required="isRequiredLegalComplianceProfileAttribute('fatca_declaration', 'provide_giin')"
            :label="$t('fatca_declaration.provide_giin')" />

        <BaseInputSelect
            v-if="
                formValues.us_entity === 'no' &&
                formValues.financial_institutions_under_fatca === 'yes' &&
                formValues.financial_institution_category &&
                formValues.provide_giin === 'no'
            "
            v-model="formValues.no_giin_reason"
            name="fatca_declaration.no_giin_reason"
            :options="noGiinReasonOptions"
            :label="$t('fatca_declaration.no_giin_reason')" />
        <div
            v-if="
                formValues.us_entity === 'no' &&
                formValues.financial_institutions_under_fatca === 'yes' &&
                formValues.financial_institution_category &&
                formValues.provide_giin === 'no' &&
                formValues.no_giin_reason === 'not_yet_obtained_but_supported'
            ">
            <BaseInputText
                v-model="formValues.giin_sponsor_name"
                name="fatca_declaration.giin_sponsor_name"
                :label="$t('fatca_declaration.giin_sponsor_name')"></BaseInputText>
            <BaseInputText
                v-model="formValues.giin_sponsor"
                name="fatca_declaration.giin_sponsor"
                :label="$t('fatca_declaration.giin_sponsor')"></BaseInputText>
        </div>
        <BaseInputText
            v-if="
                formValues.us_entity === 'no' &&
                formValues.financial_institutions_under_fatca === 'yes' &&
                formValues.financial_institution_category &&
                formValues.provide_giin === 'yes'
            "
            v-model="formValues.giin"
            name="fatca_declaration.giin"
            :label="$t('fatca_declaration.giin')"></BaseInputText>
    </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { LegalFatcaDeclaration } from '../../../../../utilities/account/useAccountDataPersonal'
import {
    getLegalWidgetFormValidationRules,
    initializeLegalWidgetForm,
    isRequiredLegalComplianceProfileAttribute,
} from './forms'
import BaseInputSelect from '../../../../base/BaseInputSelect.vue'
import InlineAlert from '../../../../base/InlineAlert.vue'
import BaseInputRadio from '../../../../base/BaseInputRadio.vue'
import { yesNoOptions } from '../../../../../data'
import BaseInputText from '../../../../base/BaseInputText.vue'
import { useVModel } from '@vueuse/core'

const nfeOptions = [
    {
        key: 'active_nfe',
        label: 'Active NFE',
    },
    {
        key: 'passive_nfe',
        label: 'Passive NFE',
    },
    {
        key: 'non_participating_nfe',
        label: 'Non-participating NFE (except non-financial foreign entity)',
    },
    {
        key: 'non_specified_us_entity',
        label: 'The entity is a U.S. entity but not a specified U.S. entity',
    },
]

const financialInstitutionCategoryOptions = [
    {
        key: 'participating_fi',
        label: 'Participating financial institution not located in the U.S. (Participating Foreign FI)',
    },
    {
        key: 'registered_fi',
        label: 'Registered FATCA compliant FI that is not located in the U.S. (Registered Deemed Compliant FFI)',
    },
    {
        key: 'reporting_model_1',
        label: 'Reporting Model 1 financial institution that is not domiciled in the U.S. (Reporting Model 1 FFI)',
    },
    {
        key: 'reporting_model_2',
        label: 'Reporting Model 2 financial institution that is not domiciled in the U.S. (Reporting Model 2 FFI)',
    },
]

const noGiinReasonOptions = [
    {
        key: 'not_yet_obtained',
        label: 'Participating financial institution and has not yet obtained GIIN, but will obtain one within 90 days',
    },
    {
        key: 'not_yet_obtained_but_supported',
        label: 'Company has not yet obtained a GIIN but is supported/sponsored by another company that does have a GIIN',
    },
    {
        key: 'fatca_exempt_beneficial_owner',
        label: 'FATCA exempt Beneficial Owner (FATCA)',
    },
    {
        key: 'non_participating_fi',
        label: 'Non-participating financial institution that is not resident in the U.S. (non-participating FFI)',
    },
    {
        key: 'non_reporting_fi',
        label: 'Non-reporting financial institution (excepted non-reporting FFI)',
    },
    {
        key: 'other_fi',
        label: 'Other financial institution',
    },
]

const props = defineProps<{
    modelValue: LegalFatcaDeclaration
}>()
const emit = defineEmits(['update:modelValue'])

const formValues = useVModel(props, 'modelValue', emit)

watch(
    () => formValues.value.us_entity,
    (newValue, oldValue) => {
        if (oldValue !== null) {
            formValues.value.financial_institutions_under_fatca = null
            formValues.value.non_financial_entity_reason = null
            formValues.value.financial_institution_category = null
            formValues.value.no_giin_reason = null
            formValues.value.provide_giin = null
            formValues.value.giin = null
            formValues.value.giin_sponsor_name = null
            formValues.value.giin_sponsor = null
        }
    }
)

watch(
    () => formValues.value.financial_institutions_under_fatca,
    (newValue, oldValue) => {
        if (oldValue !== null) {
            formValues.value.non_financial_entity_reason = null
            formValues.value.financial_institution_category = null
            formValues.value.no_giin_reason = null
            formValues.value.provide_giin = null
            formValues.value.giin = null
            formValues.value.giin_sponsor_name = null
            formValues.value.giin_sponsor = null
        }
    }
)

watch(
    () => formValues.value.financial_institution_category,
    (newValue, oldValue) => {
        if (oldValue !== null) {
            formValues.value.no_giin_reason = null
            formValues.value.provide_giin = null
            formValues.value.giin = null
            formValues.value.giin_sponsor_name = null
            formValues.value.giin_sponsor = null
        }
    }
)

watch(
    () => formValues.value.provide_giin,
    (newValue, oldValue) => {
        if (oldValue !== null) {
            formValues.value.no_giin_reason = null
            formValues.value.giin = null
            formValues.value.giin_sponsor_name = null
            formValues.value.giin_sponsor = null
        }
    }
)

watch(
    () => formValues.value.no_giin_reason,
    (newValue, oldValue) => {
        if (oldValue !== null) {
            formValues.value.giin_sponsor_name = null
            formValues.value.giin_sponsor = null
        }
    }
)

initializeLegalWidgetForm(getLegalWidgetFormValidationRules('fatca_declaration'))
</script>
